import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CurecallTextButton } from '@honestica/curecall-ui'
import { IntegerRangeCard } from './integer-range-card.component'
import { AddRangeBetweenButton } from './add-range-between-button.component'
import { RangeCoverageInfo } from './range-coverage-info.component'
import { useRangeHandlers } from './use-range.hook'
import { areAllValuesCovered } from './range.helper'
import { TNodeFormValues, TNodeIntegerRangeVariable } from '../../../node.type'
import { twMerge } from 'src/utils/tw-merge.util'
import { useChatbotFlow } from '../../../chatbot.provider'

export function IntegerRangesConditionList() {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<TNodeFormValues>()
  const { updateIntegerRangeConditionAction } = useChatbotFlow()
  const variable = useWatch({ control, name: 'variable' }) as TNodeIntegerRangeVariable

  const ranges = useMemo(() => variable.condition?.ranges || [], [variable])
  const minValue = useMemo(() => variable.minValue ?? 0, [variable])
  const maxValue = useMemo(() => variable.maxValue ?? 0, [variable])

  const {
    animatingIndex,
    autoUpdatedBounds,
    handleLowerBoundChange,
    handleUpperBoundChange,
    handleDeleteRange,
    handleCreateAllRanges,
    handleAddRangeBetween,
  } = useRangeHandlers({
    ranges,
    minValue,
    maxValue,
    setValue,
    updateRanges: updateIntegerRangeConditionAction,
  })

  const allValuesCovered = areAllValuesCovered(ranges, minValue, maxValue)

  return (
    <div className="c-flex c-flex-col c-gap-4 c-animate-fade-in c-p-4 c-border-t c-border-grey-400 c-w-full">
      <div className="c-flex c-justify-between c-items-center">
        <RangeCoverageInfo ranges={ranges} minValue={minValue} maxValue={maxValue} />
        <CurecallTextButton
          type="button"
          onClick={handleCreateAllRanges}
          isDisabled={allValuesCovered}
          className="c-text-sm"
        >
          {t('pages.configuration.followups.chatbot.conditional-sections.create-range-for-each-value')}
        </CurecallTextButton>
      </div>

      {ranges.map((range, index) => {
        const isLastRange = index === ranges.length - 1
        const canSplitCurrentRange = !isNaN(range.upperBound) && range.upperBound - range.lowerBound >= 1

        const nextRange = ranges[index + 1]
        const canSplitNextRange =
          nextRange && !isNaN(nextRange.upperBound) && nextRange.upperBound - nextRange.lowerBound >= 1

        const canSplitEitherRange = canSplitCurrentRange || canSplitNextRange

        return (
          <div
            key={`${index}-${range.lowerBound}`}
            className={twMerge('c-flex c-flex-col c-gap-2', animatingIndex === index && 'c-animate-scale-in')}
          >
            <IntegerRangeCard
              rangeIndex={index}
              isFirstRange={index === 0}
              isLastRange={isLastRange}
              minValue={minValue}
              maxValue={maxValue}
              autoUpdatedBounds={autoUpdatedBounds}
              onDelete={() => handleDeleteRange(index)}
              onLowerBoundChange={handleLowerBoundChange}
              onUpperBoundChange={handleUpperBoundChange}
            />

            {allValuesCovered && !isLastRange && canSplitEitherRange && (
              <AddRangeBetweenButton onClick={() => handleAddRangeBetween(index)} />
            )}
          </div>
        )
      })}
    </div>
  )
}
